import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Pencil, Plus } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useApi } from "@/hooks/useApi";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, FilterBar, ErrorBox, Loading, EmptyState } from "@/components/common/ui-bits";
import { FormDialog } from "@/components/common/FormDialog";
import { serviceFields } from "@/lib/schemas";
import { money, optionList, SERVICE_CATEGORIES } from "@/lib/format";

function ServiceCard({ s, isAdmin, onEdit }) {
  return (
    <div className={`card-box flex flex-col p-5 ${s.active ? "" : "opacity-60"}`} data-testid={`service-card-${s.id}`}>
      <div className="mb-2 flex items-start justify-between gap-2">
        <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600">{s.category}</span>
        {!s.active && <span className="text-xs text-slate-400">Inactivo</span>}
      </div>
      <h3 className="text-base font-bold text-slate-900">{s.name}</h3>
      <p className="mt-1 line-clamp-2 text-sm text-slate-500">{s.description}</p>
      <p className="num mt-3 text-xl font-semibold text-slate-900" data-testid={`service-price-${s.id}`}>{money(s.price)}<span className="ml-1 font-sans text-xs font-normal text-slate-400">{s.price_type}</span></p>
      {s.requires_renewal && <p className="text-xs text-slate-500">Renovación: {money(s.renewal_price)}</p>}
      {isAdmin && s.internal_cost != null && <p className="text-xs text-amber-700">Costo interno {money(s.internal_cost)}{s.margin != null && ` · margen ${money(s.margin)}`}</p>}
      {s.included?.length > 0 && <ul className="mt-3 list-inside list-disc text-xs text-slate-600">{s.included.map((i) => <li key={i}>{i}</li>)}</ul>}
      {isAdmin && <Button size="sm" variant="outline" className="mt-4 self-start" onClick={() => onEdit(s)} data-testid={`edit-service-${s.id}`}><Pencil className="mr-2 h-3.5 w-3.5" />Editar</Button>}
    </div>
  );
}

export default function Services() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [f, setF] = useState({});
  const { data, loading, error, reload } = useApi("/services", f);
  const [editing, setEditing] = useState(null);
  const fields = isAdmin ? serviceFields : serviceFields.filter((x) => x.name !== "internal_cost");

  return (
    <div>
      <PageHeader title="Servicios y paquetes" subtitle="Catálogo editable. Los precios vacíos se muestran como [PENDIENTE]."
        actions={isAdmin && <Button onClick={() => setEditing({})} data-testid="new-service-button"><Plus className="mr-2 h-4 w-4" />Nuevo servicio</Button>} />
      <FilterBar values={f} onChange={setF} testid="services-filters" filters={[{ key: "category", label: "Categoría", options: optionList(SERVICE_CATEGORIES) }]} />
      <ErrorBox text={error} />
      {loading && !data ? <Loading /> : data?.length === 0 ? <EmptyState /> : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">{(data || []).map((s) => <ServiceCard key={s.id} s={s} isAdmin={isAdmin} onEdit={setEditing} />)}</div>
      )}
      <FormDialog open={Boolean(editing)} onOpenChange={(o) => !o && setEditing(null)} title={editing?.id ? "Editar servicio" : "Nuevo servicio"} testid="service-form" fields={fields}
        initial={editing?.id ? editing : { active: true, price_type: "único" }}
        onSubmit={async (v) => {
          if (editing.id) await api.patch(`/services/${editing.id}`, v); else await api.post("/services", v);
          toast.success("Servicio guardado"); reload();
        }} />
    </div>
  );
}
